import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const UnsavedChangesContext = createContext({
    hasUnsavedChanges: false,
    markDirty() {},
    clearDirty() {},
    confirmDiscard: () => true,
});

export const useUnsavedChanges = () => useContext(UnsavedChangesContext);

export function UnsavedHistoryGuard() {
    const location = useLocation();
    const { confirmDiscard, clearDirty } = useUnsavedChanges();
    const currentIndexRef = useRef(window.history.state?.idx);
    const restoringIndexRef = useRef(null);

    useLayoutEffect(() => {
        if (restoringIndexRef.current === null) currentIndexRef.current = window.history.state?.idx;
    }, [location]);

    // Subscribe before BrowserRouter's parent layout effect installs its popstate listener.
    useLayoutEffect(() => {
        const beforeHistoryNavigation = (event) => {
            const nextIndex = event.state?.idx;
            const currentIndex = currentIndexRef.current;
            if (!Number.isInteger(nextIndex) || !Number.isInteger(currentIndex)) return;

            // Restore the existing entry before BrowserRouter can unmount the editor.
            if (restoringIndexRef.current !== null) {
                event.stopImmediatePropagation();
                const targetIndex = restoringIndexRef.current;
                if (nextIndex === targetIndex) restoringIndexRef.current = null;
                else window.history.go(targetIndex - nextIndex);
                return;
            }
            if (nextIndex === currentIndex) return;
            if (confirmDiscard()) {
                currentIndexRef.current = nextIndex;
                clearDirty();
                return;
            }

            event.stopImmediatePropagation();
            restoringIndexRef.current = currentIndex;
            window.history.go(currentIndex - nextIndex);
        };
        window.addEventListener('popstate', beforeHistoryNavigation, true);
        return () => window.removeEventListener('popstate', beforeHistoryNavigation, true);
    }, [confirmDiscard, clearDirty]);

    return null;
}

export function UnsavedChangesProvider({ children }) {
    const dirtyRef = useRef(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const markDirty = useCallback(() => {
        dirtyRef.current = true;
        setHasUnsavedChanges(true);
    }, []);
    const clearDirty = useCallback(() => {
        dirtyRef.current = false;
        setHasUnsavedChanges(false);
    }, []);
    const confirmDiscard = useCallback(() => {
        if (!dirtyRef.current) return true;
        if (!window.confirm('Есть несохранённые изменения. Покинуть редактор и потерять изменения?')) return false;
        return true;
    }, []);

    useEffect(() => {
        const beforeUnload = (event) => {
            if (!dirtyRef.current) return;
            event.preventDefault();
            event.returnValue = '';
        };
        const beforeLinkNavigation = (event) => {
            if (!dirtyRef.current || event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
            const link = event.target.closest?.('a[href]');
            if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
            const destination = new URL(link.href, window.location.href);
            if (!['http:', 'https:'].includes(destination.protocol)) return;
            if (destination.origin === window.location.origin && destination.pathname === window.location.pathname && destination.search === window.location.search) return;
            if (!confirmDiscard()) {
                event.preventDefault();
                event.stopPropagation();
            } else {
                clearDirty();
            }
        };
        window.addEventListener('beforeunload', beforeUnload);
        document.addEventListener('click', beforeLinkNavigation, true);
        return () => {
            window.removeEventListener('beforeunload', beforeUnload);
            document.removeEventListener('click', beforeLinkNavigation, true);
        };
    }, [confirmDiscard, clearDirty]);

    const value = useMemo(() => ({ hasUnsavedChanges, markDirty, clearDirty, confirmDiscard }), [hasUnsavedChanges, markDirty, clearDirty, confirmDiscard]);
    return <UnsavedChangesContext.Provider value={value}>{children}</UnsavedChangesContext.Provider>;
}
